import moment from 'moment';
import 'moment-duration-format';
import Constants from '../../common/constants/Constants';

const Utils = {};

Utils.DateAsUtcFe = (localTime) => {
  return moment(localTime).utc().subtract(moment(localTime).utcOffset(), 'm');
};

Utils.DateFromUtcFe = (localTime) => {
  return moment(localTime).utc().add(moment(localTime).utcOffset(), 'm');
};

Utils.NowAsUtcFe = () => Utils.DateAsUtcFe(moment());

Utils.FeDateAsUtcForBeISOString = (localTime) => {
  return moment(localTime).utc().add(moment(localTime).utcOffset(), 'm').toISOString();
};

Utils.DateAsInstrumentTimeFe = (localTime, instrumentUtcOffset) => {
  return Utils.DateAsUtcFe(localTime).subtract(instrumentUtcOffset, 'h');
};

Utils.NowAsInstrumentTime = () => Utils.DateAsInstrumentTimeFe(moment());

Utils.IsDateTime = (value) => {
  return (
    moment.isDate(value) || moment.isMoment(value) || moment(value, moment.ISO_8601, true).isValid()
  );
};

Utils.FormatDateTime = (value, settings) => {
  if (!settings) {
    return value;
  }

  if (!value) {
    return '';
  }

  if (typeof settings === 'string') {
    return typeof value === 'string'
      ? moment.parseZone(value).format(settings)
      : moment(value).format(settings);
  }

  var globalDateTimeSettings = settings[Constants.SETTING_KEYS.DATE_FORMAT];
  if (!globalDateTimeSettings) {
    return value;
  }

  return typeof value === 'string'
    ? moment.parseZone(value).format(globalDateTimeSettings)
    : moment(value).format(globalDateTimeSettings);
};

Utils.FormatDate = (value, settings) => {
  if (!settings) {
    return value;
  }

  if (!value) {
    return '';
  }

  if (typeof settings === 'string') {
    return typeof value === 'string'
      ? moment.parseZone(value).format(settings)
      : moment(value).format(settings);
  }

  var dateFormatFromSettings = settings.DateFormat.split(' ')[0]; //use only the format option for the date, exclude time

  if (dateFormatFromSettings) {
    return typeof value === 'string'
      ? moment.parseZone(value).format(dateFormatFromSettings)
      : moment(value).format(dateFormatFromSettings);
  }

  var globalDateTimeSettings = settings[Constants.SETTING_KEYS.DATE_FORMAT];
  if (!globalDateTimeSettings) {
    return value;
  }

  var dateFormat = globalDateTimeSettings.split(' ')[0];

  return typeof value === 'string'
    ? moment.parseZone(value).format(dateFormat)
    : moment(value).format(dateFormat);
};

Utils.FormatNumber = (value, settings) => {
  if (!settings) {
    return value;
  }

  if (value === undefined) {
    return '';
  }

  var settingType = typeof settings;
  if (settingType === 'string' || settingType === 'number') {
    return +(+value).toFixed(+settings);
  }

  var globalDecimalFormatSettings = settings[Constants.SETTING_KEYS.NUMBER_PRECISION];
  if (!globalDecimalFormatSettings) {
    return value;
  }

  return +(+value).toFixed(+globalDecimalFormatSettings);
};

Utils.FormatDuration = (value, settings) => {
  if (!settings) {
    return value;
  }

  var duration = moment.duration(value);

  if (!duration || !duration.isValid()) {
    return value;
  }

  var settingType = typeof settings;
  if (settingType === 'string' || settingType === 'number') {
    return duration.format(settings);
  }

  var globalDurationSettings = settings[Constants.SETTING_KEYS.DURATION_FORMAT];
  if (!globalDurationSettings) {
    return value;
  }

  return duration.format(globalDurationSettings);
};

Utils.exportToCsv = (filename, rows) => {
  const processRow = (row) => {
    let finalVal = '';

    for (let j = 0; j < row.length; j++) {
      let innerValue = row[j] || row[j] === false || row[j] === 0 ? row[j].toString() : '';

      if (row[j] instanceof Date) {
        innerValue = row[j].toLocaleString();
      }

      let result = innerValue.replace(/"/g, '""');

      if (result.search(/("|,|\n)/g) >= 0) {
        result = '"' + result + '"';
      }

      if (j > 0) {
        finalVal += ',';
      }

      finalVal += result;
    }

    return finalVal + '\r\n';
  };

  let csvFile = '';

  for (let i = 0; i < rows.length; i++) {
    csvFile += processRow(rows[i]);
  }

  const blob = new Blob([csvFile], { type: 'text/csv;charset=utf-8;' });

  if (navigator.msSaveBlob) {
    navigator.msSaveBlob(blob, filename);
  } else {
    const link = document.createElement('a');

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
};

Utils.determineCurrentPath = (pathname) => {
  return pathname.substr(pathname.lastIndexOf('/'));
};

Utils.determineHourMinutesSecondsFromTicks = (ticks) => {
  const seconds = Math.floor(ticks / 10000000);
  const hours = Math.floor(seconds / 3600);
  const remainingSeconds = seconds % 3600;
  const minutes = Math.floor(remainingSeconds / 60);
  const finalSeconds = remainingSeconds % 60;
  const displayHours = hours < 10 ? `0${hours}` : `${hours}`;
  const displayMinutes = minutes < 10 ? `0${minutes}` : `${minutes}`;
  const displaySeconds = finalSeconds < 10 ? `0${finalSeconds}` : `${finalSeconds}`;
  return `${displayHours}:${displayMinutes}:${displaySeconds}`;
};

Utils.determineDaysHourMinSecFromTicks = (ticks) => {
  var sec = Math.floor(ticks / 10000000);
  return (
    (sec >= 86400 ? Math.floor(sec / 86400) + 'd ' : '') +
    (sec >= 3600 ? Math.floor((sec / 3600) % 24) + 'h ' : '') +
    (sec >= 60 ? Math.floor((sec / 60) % 60) + 'm ' : '') +
    (sec >= 1 ? (sec % 60) + 's' : 0)
  );
};

Utils.shallowCompare = (obj1, obj2) =>
  Object.keys(obj1).length === Object.keys(obj2).length &&
  Object.keys(obj1).every((key) => obj2.hasOwnProperty(key) && obj1[key] === obj2[key]);

export default Utils;
